import React from 'react';
import { PatientObj } from 'pages/Dashboard/types/patient.types';
import { TableCell, TableRow, Typography } from '@mui/material';
import ResendInvite from 'pages/Dashboard/components/ResendInvite';
import { formatPhoneNumber } from 'utils/phoneUtils';
import { capitalize } from 'utils/appUtils';
import Theme from 'theme';

interface PatientComponentProps {
  patient: PatientObj;
}

// Returns true if the patient is less than 13 years old based on their dateOfBirth (GoogleDate)
export function isPatientUnder13(dateOfBirth?: {
  year: number;
  month: number;
  day: number;
}): boolean {
  if (!dateOfBirth) return false;
  const dob = new Date(dateOfBirth.year, dateOfBirth.month - 1, dateOfBirth.day);
  if (Number.isNaN(dob.getTime())) return false;
  const today = new Date();
  let age = today.getFullYear() - dob.getFullYear();
  const m = today.getMonth() - dob.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < dob.getDate())) {
    age -= 1;
  }
  return age < 13;
}

function PendingPatientAction({ patient, isUnder13 }: { patient: PatientObj; isUnder13: boolean }) {
  if (isUnder13) {
    return <Typography sx={{ color: 'grey' }}>Cannot send invite to patients under 13</Typography>;
  }
  if (patient.onboardingMetadata?.status !== 'PREPARING_INVITE_DELIVERY') {
    return <ResendInvite patient={patient} />;
  }
  return (
    <Typography sx={{ color: 'grey' }}>No action available while invite in progress</Typography>
  );
}

function PendingPatientComponent(props: PatientComponentProps) {
  const { patient } = props;
  const isNamePending = !patient?.name?.firstName;
  const isUnder13 = isPatientUnder13(patient?.dateOfBirth);
  // Convert phone number to string or display fallback if missing
  const formattedPhoneNumber = formatPhoneNumber(patient.phoneNumber);
  const phoneNumberString = `${patient.alias} (${String(
    formattedPhoneNumber || 'No Number Available',
  )})`;
  const nameString = `${capitalize(patient.name?.firstName ?? '')} ${capitalize(
    patient.name?.lastName ?? '',
  )}`;
  const isErrorPatient = patient.onboardingMetadata?.status === 'INVITE_DELIVERY_FAILED';

  return (
    <TableRow
      sx={{
        '&:hover': {
          backgroundColor: '#f2f2f2',
        },
      }}
    >
      <TableCell
        key={patient.id}
        scope='patient'
        className='fs-mask'
        sx={{
          color: 'grey',
          fontWeight: patient.hasRead ? 600 : 400,
          padding: '4px 8px',
          width: 400,
        }}
        color=''
      >
        {isNamePending ? phoneNumberString : nameString}
      </TableCell>
      <TableCell sx={{ padding: '4px 8px', ml: 5 }}>
        <Typography sx={{ ml: 0, textAlign: 'center' }}>
          {patient.dateAdded ? new Date(patient.dateAdded).toLocaleDateString('en-US') : ''}
        </Typography>
      </TableCell>
      <TableCell sx={{ padding: '4px 8px', ml: 5 }}>
        <Typography
          fontSize={13}
          sx={{
            ml: 0,
            textAlign: 'center',
            p: 0.75,
            justifySelf: 'center',
            borderRadius: 4,
            color: Theme.custom.colors.lightTextPrimary,
            backgroundColor:
              isErrorPatient ? '#FECDD2' : '#E9F9EA',
          }}
        >
          {isErrorPatient
            ? 'undeliverable'
            : 'pending'}
        </Typography>
      </TableCell>
      <TableCell sx={{ padding: '4px 8px' }}>
        <Typography sx={{ ml: 0, textAlign: 'center' }}>
          {patient.inviteDeliveryDate
            ? new Date(patient.inviteDeliveryDate).toLocaleDateString('en-US')
            : ''}
        </Typography>
      </TableCell>
      <TableCell color='grey' sx={{ padding: '10px 4px' }}>
        <PendingPatientAction patient={patient} isUnder13={isUnder13} />
      </TableCell>
    </TableRow>
  );
}

export default PendingPatientComponent;
